import { IntlFormatters } from 'react-intl'

import {
  AggregationFeatures,
  AggregationGrades,
  AggregationReview,
} from '@awaze/mfe-common.types-and-mocks.one-search-client'
import type {
  ICommonAggregations,
  SearchCriteria,
} from '@awaze/mfe-common.types-and-mocks.site-search-service'
import { FilterProps } from '@awazevr/design-system.components.filter'
import { Grade } from '@awazevr/design-system.components.grade'
import { TmsContextType } from '@awazevr/mfe-common.contexts.tms'

import { ALLOWED_GRADES, GRADE_CAT_IDS } from '@/constants'

import {
  BuildGradesArrayProps,
  ClickedFeature,
  FeatureGroupIds,
  ProcessedFeature,
} from './Filters.types'

export const getPopularFeatures = (
  combinedAndSortedFeatures: ProcessedFeature[],
  popularFeatures: Array<string>,
): ProcessedFeature[] => [
  ...combinedAndSortedFeatures.filter((el) =>
    popularFeatures.includes(el.featureId),
  ),
]

export const getPopularDestinations = (
  combinedAndSortedFeatures: ProcessedFeature[],
): ProcessedFeature[] =>
  combinedAndSortedFeatures.filter(
    (el) => el.featureCode?.toUpperCase() === 'AONB',
  )

export const getAllFeatures = (
  combinedAndSortedFeatures: ProcessedFeature[],
): ProcessedFeature[] =>
  combinedAndSortedFeatures.filter(
    (el) => el.featureCode?.toUpperCase() !== 'AONB',
  )

export const FREE_CANCELLATION_FEATURE_ID_NOVASOL_V2 = 'ent_freecancellation'

export const processFeaturesAndSiteFeatures = (
  aggregationsFeatures: Array<AggregationFeatures> = [],
  aggregationsSiteFeatures: Array<AggregationFeatures> = [],
  hideFreeCancellation: boolean = false, // HACK: GD_128_ONESEARCH_V2 This needs to be removed once OneSearch V2 supports dated features. Ask Blake or Liam for more details
): Array<ProcessedFeature> => {
  const combinedFeatures = [
    ...aggregationsFeatures,
    ...aggregationsSiteFeatures,
  ]
  const processedFeatures = combinedFeatures
    .reduce<Array<ProcessedFeature>>((result, feature) => {
      //TODO remove all of the feature string conversion in filters logic
      const featureId = String(feature.featureId)

      // HACK: GD_128_ONESEARCH_V2 This needs to be removed once OneSearch V2 supports dated features. Ask Blake or Liam for more details
      if (
        featureId === FREE_CANCELLATION_FEATURE_ID_NOVASOL_V2 &&
        hideFreeCancellation
      ) {
        return result
      }

      // HACK: Remapping Pets Allowed features until fixed in the dataset
      if (featureId === '9244') {
        result.push({
          ...feature,
          featureName: 'Pet Friendly',
          featureId,
        })
      } else if (featureId === '9737') {
        result.push({
          ...feature,
          featureName: 'Pets – not allowed',
          featureId: featureId,
        })
      } else {
        result.push({
          ...feature,
          featureId: featureId,
        })
      }

      return result
    }, [])
    .sort((a, b) => (a.count < b.count ? 1 : -1))

  return processedFeatures
}

export const bathroomMapper = (input: string | number): string | number => {
  const bathroomCatIds = [
    'onebath',
    'twobath',
    'threebath',
    'fourbath',
    'fivebath',
    'sixplusbath',
  ]

  if (typeof input === 'string') {
    return bathroomCatIds.indexOf(input) + 1 || 1
  }

  return bathroomCatIds[input - 1] || 'onebath'
}

export const bathroomUnmapper = (input: string): number | null => {
  const bathroomCategories = {
    onebath: 1,
    twobath: 2,
    threebath: 3,
    fourbath: 4,
    fivebath: 5,
    sixplusbath: 6,
  }

  type BathroomKeys = keyof typeof bathroomCategories

  const isBathroomKey = (key: string): key is BathroomKeys => {
    return key in bathroomCategories
  }

  if (isBathroomKey(input)) {
    return bathroomCategories[input]
  }

  return null
}

export const bedroomMapper = (input: string | number): string | number => {
  const bedroomCatIds = [
    'onebedexcludingstudio',
    'twobed',
    'threebed',
    'fourbed',
    'fivebed',
    'sixplusbed',
  ]

  if (typeof input === 'string') {
    return bedroomCatIds.indexOf(input) + 1 || 1
  }

  return bedroomCatIds[input - 1] || 'onebedexcludingstudio'
}

export const bedroomUnmapper = (input: string): number | null => {
  const bedroomCategories = {
    onebedexcludingstudio: 1,
    twobed: 2,
    threebed: 3,
    fourbed: 4,
    fivebed: 5,
    sixplusbed: 6,
  }

  type BedroomKeys = keyof typeof bedroomCategories

  const isBedroomKey = (key: string): key is BedroomKeys => {
    return key in bedroomCategories
  }

  if (isBedroomKey(input)) {
    return bedroomCategories[input]
  }

  return null
}

type GradeKeys = keyof typeof GRADE_CAT_IDS

const isGradeKey = (key: number): key is GradeKeys => {
  return key in GRADE_CAT_IDS
}

export const gradeMapper = (grade: string): string | null => {
  const gradeNumber = Number(grade)
  if (isGradeKey(gradeNumber)) {
    return GRADE_CAT_IDS[gradeNumber]
  }

  return null
}

export const getNextRHSLevel = (currentRHSLevel: string): string => {
  switch (currentRHSLevel) {
    case 'rhs1':
      return 'rhs2'
    case 'rhs2':
      return 'rhs3'
    case 'rhs3':
      return 'rhs5'
    case 'rhs5':
      return 'rhs5'
    default:
      return 'rhs1'
  }
}

export const removeUnsupportedGrades = ({
  grade,
}: AggregationGrades): boolean =>
  typeof grade === 'string' &&
  !isNaN(Number(grade)) &&
  ALLOWED_GRADES.includes(grade)

export const buildGradesArray = ({
  grades,
  display = false,
}: BuildGradesArrayProps): Array<FilterProps> =>
  grades
    .filter((grade) => removeUnsupportedGrades(grade))
    .sort((grade1, grade2) => parseInt(grade2.grade) - parseInt(grade1.grade))
    .map((grade) => ({
      count: grade.count,
      featureName: display ? (
        <Grade
          translations={{ stars: 'stars', outOf: 'out of' }}
          grade={parseInt(grade.grade)}
        />
      ) : (
        `${grade.grade} stars`
      ),
      featureId: gradeMapper(grade.grade) || '',
      ariaLabel: `${grade.grade} stars`,
    }))

interface RoomReducerArgs {
  accumulator: Array<FilterProps>
  count: number
  formatMessage: IntlFormatters['formatMessage']
  mapper: (input: string | number) => string | number
  roomCount: number
}

export const roomReducer = ({
  accumulator,
  count,
  formatMessage,
  mapper,
  roomCount,
}: RoomReducerArgs): Array<FilterProps> => {
  const sixItemsOrAboveFilterDoesNotExist = !accumulator.find(
    (cat) =>
      cat.featureName ===
      formatMessage({ id: 'filter.or-more.title' }, { number: 6 }),
  )
  const is6ItemsOrAbove = roomCount > 6

  if (
    !is6ItemsOrAbove ||
    (is6ItemsOrAbove && sixItemsOrAboveFilterDoesNotExist)
  ) {
    const currentValue = is6ItemsOrAbove ? 6 : roomCount
    accumulator.push({
      count: count,
      featureName: formatMessage(
        { id: 'filter.or-more.title' },
        { number: currentValue },
      ),
      featureId: mapper(is6ItemsOrAbove ? 6 : roomCount),
    })
  } else {
    accumulator[accumulator.length - 1].count += count
  }

  return accumulator
}

export interface BuildBathroomArrayArgs {
  formatMessage: IntlFormatters['formatMessage']
  rooms?: ICommonAggregations['bathrooms']
}

export const buildBathroomArray = ({
  formatMessage,
  rooms = [],
}: BuildBathroomArrayArgs): Array<FilterProps> => {
  const sortedRooms = [...rooms].sort(
    (firstEl, secondEl) => firstEl.bathrooms - secondEl.bathrooms,
  )

  return sortedRooms
    .filter((filter) => filter.bathrooms > 0)
    .reduce<Array<FilterProps>>(
      (accumulator, category) =>
        roomReducer({
          accumulator,
          count: category.count,
          formatMessage,
          mapper: bathroomMapper,
          roomCount: category.bathrooms,
        }),
      [],
    )
}

export interface BuildBedroomArrayArgs {
  formatMessage: IntlFormatters['formatMessage']
  rooms?: ICommonAggregations['bedrooms']
}

export const buildBedroomArray = ({
  formatMessage,
  rooms = [],
}: BuildBedroomArrayArgs): Array<FilterProps> => {
  const sortedRooms = [...rooms].sort(
    (firstEl, secondEl) => firstEl.bedrooms - secondEl.bedrooms,
  )

  return sortedRooms
    .filter((filter) => filter.bedrooms > 0)
    .reduce<Array<FilterProps>>(
      (accumulator, category) =>
        roomReducer({
          accumulator,
          count: category.count,
          formatMessage,
          mapper: bedroomMapper,
          roomCount: category.bedrooms,
        }),
      [],
    )
}

export const buildSelectedFilters = (
  aggregationFeatures: Array<AggregationFeatures>,
  selectedFeatures: SearchCriteria['features'] = [],
): Array<AggregationFeatures> =>
  aggregationFeatures.filter(({ featureId }) =>
    selectedFeatures.includes(featureId),
  )

const MIN_SHOWN_REVIEW_SCORE = 2
const MAX_SHOWN_REVIEW_SCORE = 5

export const buildReviewsArray = (
  input: Array<AggregationReview> = [],
): Array<FilterProps> => {
  const results: Array<FilterProps> = []
  input
    .filter(
      (review) =>
        review.score >= MIN_SHOWN_REVIEW_SCORE &&
        review.score <= MAX_SHOWN_REVIEW_SCORE,
    )
    .forEach((review, index, filtered) => {
      if (index < filtered.length - 1) {
        const bothScoresHaveCount = review.count && filtered[index + 1].count
        if (bothScoresHaveCount) {
          results.push({
            count: review.count + filtered[index + 1].count,
            featureName: `${review.score}-${filtered[index + 1].score}`,
            featureId: `${review.score}-${filtered[index + 1].score}`,
          })
        }
      }
    })
  return results.reverse()
}

export const isSiteFeature = (
  clickedFeature: ClickedFeature,
  siteFeatures: ICommonAggregations['features'],
): boolean =>
  siteFeatures?.some(
    (feature) => feature.featureId === clickedFeature.featureId,
  )

export const isAccommodationFeature = (
  clickedFeature: ClickedFeature,
  accommodationTypesAsFeatures: ICommonAggregations['features'],
): boolean =>
  accommodationTypesAsFeatures?.some(
    (feature) => feature.featureId === clickedFeature.featureId,
  )

type FeatureWithCount = { count: number }

export const setFeatureCountsToZero = <T extends FeatureWithCount>(
  features: T[],
): T[] => {
  return features.map((feature) => ({
    ...feature,
    count: 0,
  }))
}

export const handleDLEventOnAccordionClick = (
  tms: TmsContextType,
  isOpen: boolean,
  name: string,
): void => {
  if (isOpen) {
    tms.updateDataLayer('link', 'facet-accordion down', {
      facet_key: name,
    })
  } else {
    tms.updateDataLayer('link', 'facet-accordion up', { facet_key: name })
  }
}

export const containsYear2027 = (str: string): boolean => /2027/.test(str)
export const containsYear2025 = (str: string): boolean => /2025/.test(str)

interface GetFeatureGroupIdArgs {
  featureId: string | null
  popularDestinationIds: Array<string>
  popularFeatureIds: Array<string>
}

export const getFeatureGroupId = ({
  featureId,
  popularDestinationIds,
  popularFeatureIds,
}: GetFeatureGroupIdArgs): FeatureGroupIds => {
  if (!featureId) {
    return 'allFeatures'
  }

  if (popularDestinationIds.includes(featureId)) {
    return 'popularDestinations'
  }

  if (popularFeatureIds.includes(featureId)) {
    return 'popularFeatures'
  }

  return 'allFeatures'
}

export const getOrderedFeatures = (
  features: ProcessedFeature[],
  isInAlphabeticalFiltersExp: boolean,
  shouldShowZeroFeaturesCount: boolean,
): ProcessedFeature[] => {
  const ordered = isInAlphabeticalFiltersExp
    ? features
        .slice()
        .sort((a, b) =>
          (a.featureName ?? '').localeCompare(b.featureName ?? ''),
        )
    : features

  return shouldShowZeroFeaturesCount ? setFeatureCountsToZero(ordered) : ordered
}
